import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { fetchDashboardAnalytics } from "../../services";
import { Loader } from "../../components/Loader";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";

// ── Event type meta ──
const eventTypeMeta = {
  buy_now: { icon: "🛒", label: "Buy Now", color: "#10b981" },
  ask_price: { icon: "💰", label: "Ask Price", color: "#f59e0b" },
  Make_payment: { icon: "💳", label: "Make Payment", color: "#3b82f6" },
  popup_close: { icon: "❌", label: "Popup Close", color: "#6b7280" },
  add_to_cart: { icon: "🛍️", label: "Add to Cart", color: "#8b5cf6" },
  view_product: { icon: "👁️", label: "View Product", color: "#06b6d4" },
  search: { icon: "🔍", label: "Search", color: "#6366f1" },
  checkout: { icon: "🛒", label: "Checkout", color: "#ec4899" },
};

const getEventMeta = (type) =>
  eventTypeMeta[type] || { icon: "📌", label: type?.replace(/_/g, " ") || "Unknown", color: "#6b7280" };

const EVENT_TYPE_OPTIONS = [
  { value: "", label: "All Events" },
  { value: "buy_now", label: "🛒 Buy Now" },
  { value: "ask_price", label: "💰 Ask Price" },
  { value: "Make_payment", label: "💳 Make Payment" },
  { value: "popup_close", label: "❌ Popup Close" },
  { value: "add_to_cart", label: "🛍️ Add to Cart" },
  { value: "view_product", label: "👁️ View Product" },
  { value: "search", label: "🔍 Search" },
];


const COLORS = ["#10b981","#3b82f6","#f59e0b","#8b5cf6","#ec4899","#06b6d4","#6b7280","#ef4444","#14b8a6","#f97316","#6366f1","#84cc16"];

const QUICK_PRESETS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

// ── Helpers ──
const formatDate = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return formatDate(d); };

// ── Chart defaults ──
const chartOpts = { renderer: "canvas" };
const chartTheme = {
  tooltip: { trigger: "axis", backgroundColor: "rgba(255,255,255,0.96)", borderColor: "#e5e7eb", borderWidth: 1, textStyle: { color: "#1f2937", fontSize: 12 } },
  grid: { left: "3%", right: "4%", bottom: "8%", top: "8%", containLabel: true },
  animationDuration: 800,
  animationEasing: "cubicOut",
};

// ── StatCard (production) ──
const StatCard = ({ icon, label, value, trend, color }) => (
  <div className="group relative bg-white rounded-2xl border border-gray-100 p-4 sm:p-5 flex items-center gap-3 sm:gap-4 transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 hover:border-gray-200 overflow-hidden">
    {/* Gradient accent bar */}
    <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${color} opacity-60`} />
    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${color} shadow-md shadow-black/5`}>
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-widest">{label}</p>
      <p className="text-lg sm:text-2xl font-extrabold text-gray-800 mt-0.5 tracking-tight">{value}</p>
      {trend && <p className="text-[11px] text-gray-400 mt-px truncate">{trend}</p>}
    </div>
  </div>
);

// ── Skeleton placeholder for charts ──
const ChartSkeleton = ({ height = 280 }) => (
  <div className="animate-pulse space-y-3" style={{ height }}>
    <div className="flex gap-3">
      <div className="h-3 w-20 bg-gray-100 rounded-full" />
      <div className="h-3 w-32 bg-gray-100 rounded-full" />
    </div>
    <div className={`bg-gray-50 rounded-xl flex items-center justify-center`} style={{ height: height - 30 }}>
      <svg className="w-10 h-10 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    </div>
  </div>
);

// ── Refresh spinner ──
const RefreshSpinner = () => (
  <div className="inline-flex items-center gap-1.5 text-teal-500 animate-pulse">
    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
    <span className="text-[10px] font-medium">Refreshing…</span>
  </div>
);

// ── Chart wrapper with responsive height ──
const ChartCard = ({ title, subtitle, option, height = 300, loading = false, refreshing = false, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5 transition-all duration-300 hover:shadow-lg hover:border-gray-200 group relative">
    <div className="flex items-start justify-between mb-1">
      <div>
        <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-current opacity-60" />
          {title}
        </h3>
        {subtitle && <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {refreshing && <RefreshSpinner />}
        {option && !refreshing && (
          <span className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px] text-gray-300 bg-gray-50 px-2 py-0.5 rounded-full">
            Updated live
          </span>
        )}
      </div>
    </div>
    <div className="mt-2 relative">
      {loading ? (
        <ChartSkeleton height={height} />
      ) : refreshing ? (
        <div className="relative" style={{ height }}>
          {/* Keep chart visible but dimmed */}
          <div className="absolute inset-0 bg-white/40 rounded-xl z-10 flex items-center justify-center" />
          {option ? (
            <ReactECharts option={option} opts={chartOpts} style={{ height, opacity: 0.5 }} notMerge />
          ) : children ? (
            <div style={{ opacity: 0.5 }}>{children}</div>
          ) : null}
        </div>
      ) : option ? (
        <ReactECharts option={option} opts={chartOpts} style={{ height }} notMerge />
      ) : children ? (
        children
      ) : (
        <div className="flex items-center justify-center" style={{ height }}>
          <div className="text-center">
            <svg className="w-8 h-8 mx-auto text-gray-200 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-2.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-xs text-gray-300">No data</p>
          </div>
        </div>
      )}
    </div>
  </div>
);

// ── Treemap helper ──
const buildTreemap = (rawData, labelKey, colors, maxLabelLen) => {
  if (!rawData?.length) return null;
  const sorted = [...rawData].sort((a, b) => b.count - a.count);
  const total = sorted.reduce((s, d) => s + d.count, 0);
  const maxCount = sorted[0].count || 1;
  return {
    tooltip: {
      trigger: "item",
      formatter: (p) => p.data ? `<strong>${p.name}</strong><br/>Visitors: <strong>${p.value}</strong> (${((p.value/total)*100).toFixed(1)}%)` : "",
      backgroundColor: "rgba(255,255,255,0.97)", borderColor: "#e5e7eb", borderWidth: 1, textStyle: { color: "#1f2937", fontSize: 12 },
    },
    series: [{
      type: "treemap", roam: false, width: "98%", height: "90%", left: "1%", top: "5%",
      breadcrumb: { show: false },
      label: {
        show: true,
        formatter: (p) => p.data ? `${maxLabelLen && p.name.length > maxLabelLen ? p.name.substring(0, maxLabelLen)+"..." : p.name}\n${p.value}` : "",
        fontSize: 11, fontWeight: "bold", color: "#fff", textShadowColor: "rgba(0,0,0,0.3)", textShadowBlur: 2,
      },
      itemStyle: { borderRadius: 6, borderColor: "#fff", borderWidth: 3 },
      visualMin: 0, visualMax: maxCount, color: colors,
      data: sorted.map((d) => ({ name: d[labelKey] || "Unknown", value: d.count })),
    }],
  };
};

// ═══════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════

const UserAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const hasLoadedRef = useRef(false);

  // Filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [eventType, setEventType] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [browserFilter, setBrowserFilter] = useState("");

  const fetchData = useCallback(async (filters) => {
    // Subsequent fetches (auto-refresh) use subtle spinner, not full skeleton
    if (hasLoadedRef.current) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError("");
    try {
      const result = await fetchDashboardAnalytics(filters);
      setData(result);
      hasLoadedRef.current = true;
    } catch {
      setError("Failed to load analytics. Please try again.");
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData({}); }, [fetchData]);

  const handleApply = () => {
    const f = {};
    if (fromDate) f.from_date = fromDate;
    if (toDate) f.to_date = toDate;
    if (eventType) f.event_type = eventType;
    if (countryFilter) f.country = countryFilter;
    if (browserFilter) f.browser = browserFilter;
    fetchData(f);
  };

  const handleReset = () => {
    setFromDate(""); setToDate(""); setEventType("");
    setCountryFilter(""); setBrowserFilter("");
    fetchData({});
  };

  const handlePreset = (days) => {
    setFromDate(daysAgo(days));
    setToDate(formatDate(new Date()));
    // Auto-apply after a short delay so state updates settle
    setTimeout(() => {
      const f = {};
      f.from_date = daysAgo(days);
      f.to_date = formatDate(new Date());
      if (eventType) f.event_type = eventType;
      if (countryFilter) f.country = countryFilter;
      if (browserFilter) f.browser = browserFilter;
      fetchData(f);
    }, 0);
  };

  const filtersActive = fromDate || toDate || eventType || countryFilter || browserFilter;
  const summary = data?.summary || {};
  const hasData = data && data.summary?.total_events > 0;

  // ── Stored initial (unfiltered) options for country & browser ──
  const [initialCountryOptions, setInitialCountryOptions] = useState([]);
  const [initialBrowserOptions, setInitialBrowserOptions] = useState([]);

  // Capture the unfiltered options once when data first arrives
  useEffect(() => {
    if (data?.country_distribution?.length && initialCountryOptions.length === 0) {
      const opts = [...new Set(data.country_distribution.map(d => d.country).filter(Boolean))].sort();
      setInitialCountryOptions(opts);
    }
  }, [data, initialCountryOptions.length]);

  useEffect(() => {
    if (data?.browser_distribution?.length && initialBrowserOptions.length === 0) {
      const opts = [...new Set(data.browser_distribution.map(d => d.browser).filter(Boolean))].sort();
      setInitialBrowserOptions(opts);
    }
  }, [data, initialBrowserOptions.length]);

  // Use these for the dropdown options (they stay stable even after filtering)
  const countryOptions = initialCountryOptions;
  const browserOptions = initialBrowserOptions;

  // ── Auto-refetch when country/browser filter changes ──
  // (state_distribution & city_distribution must be filtered server-side
  //  since they don't carry a country field for client-side filtering)
  useEffect(() => {
    if (!countryFilter && !browserFilter) return;
    const timer = setTimeout(() => {
      const f = {};
      if (fromDate) f.from_date = fromDate;
      if (toDate) f.to_date = toDate;
      if (eventType) f.event_type = eventType;
      if (countryFilter) f.country = countryFilter;
      if (browserFilter) f.browser = browserFilter;
      fetchData(f);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryFilter, browserFilter]);

  // ── Client-side filtered data (for charts that can be filtered without server round-trip) ──
  const filteredData = useMemo(() => {
    if (!data) return null;
    // Shallow clone so we don't mutate the original
    const fd = { ...data };

    // Country filter — filter country_distribution for world map (immediate feedback)
    if (countryFilter && fd.country_distribution) {
      fd.country_distribution = fd.country_distribution.filter(
        (d) => d.country === countryFilter
      );
    }

    // Browser filter — filter browser_distribution for pie chart (immediate feedback)
    if (browserFilter && fd.browser_distribution) {
      fd.browser_distribution = fd.browser_distribution.filter(
        (d) => d.browser === browserFilter
      );
    }

    // state_distribution & city_distribution are passed through as-is.
    // When auto-fetch completes, the server will return data already
    // filtered by country, so these will be correct.

    return fd;
  }, [data, countryFilter, browserFilter]);

  // ── Chart options ──

  const eventsOverTimeOption = useMemo(() => {
    if (!filteredData?.events_over_time?.length) return null;
    const days = filteredData.events_over_time.map((d) => new Date(d.day).toLocaleDateString("en-IN", { day: "numeric", month: "short" }));
    const values = filteredData.events_over_time.map((d) => d.events);
    return {
      ...chartTheme,
      tooltip: { ...chartTheme.tooltip, trigger: "axis" },
      xAxis: { type: "category", data: days, axisLabel: { fontSize: 10, color: "#9ca3af" }, axisLine: { lineStyle: { color: "#f3f4f6" } } },
      yAxis: { type: "value", minInterval: 1, axisLabel: { fontSize: 10, color: "#9ca3af" }, splitLine: { lineStyle: { color: "#f9fafb", type: "dashed" } } },
      series: [
        {
          type: "line",
          data: values,
          smooth: true, symbol: "circle", symbolSize: 6,
          lineStyle: { color: "#10b981", width: 2.5 },
          areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: "rgba(16,185,129,0.25)" }, { offset: 1, color: "rgba(16,185,129,0.02)" }] } },
          itemStyle: { color: "#10b981" },
          markLine: data.events_over_time.length > 1 ? { silent: true, data: [{ type: "average", name: "Avg" }], lineStyle: { color: "#f59e0b", type: "dashed", width: 1 }, label: { formatter: "Avg: {c}", fontSize: 9, color: "#f59e0b" } } : undefined,
        },
      ],
    };
  }, [data]);

  // Cumulative line chart — shows running total
  const cumulativeOption = useMemo(() => {
    if (!filteredData?.events_over_time?.length) return null;
    const days = filteredData.events_over_time.map((d) => new Date(d.day).toLocaleDateString("en-IN", { day: "numeric", month: "short" }));
    let running = 0;
    const cumulative = filteredData.events_over_time.map((d) => { running += d.events; return running; });
    return {
      ...chartTheme,
      tooltip: { ...chartTheme.tooltip, trigger: "axis" },
      xAxis: { type: "category", data: days, axisLabel: { fontSize: 10, color: "#9ca3af" }, axisLine: { lineStyle: { color: "#f3f4f6" } } },
      yAxis: { type: "value", minInterval: 1, axisLabel: { fontSize: 10, color: "#9ca3af" }, splitLine: { lineStyle: { color: "#f9fafb", type: "dashed" } } },
      series: [{
        type: "line",
        data: cumulative,
        smooth: true, symbol: "diamond", symbolSize: 6,
        lineStyle: { color: "#8b5cf6", width: 2.5 },
        areaStyle: { color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: "rgba(139,92,246,0.2)" }, { offset: 1, color: "rgba(139,92,246,0.02)" }] } },
        itemStyle: { color: "#8b5cf6" },
        markPoint: { data: [{ type: "max", name: "Max" }, { type: "min", name: "Min" }], symbol: "pin", symbolSize: 40, label: { fontSize: 9, color: "#fff" } },
      }],
    };
  }, [data]);

  const hourlyActivityOption = useMemo(() => {
    if (!filteredData?.hourly_activity?.length) return null;
    const values = Array(24).fill(0);
    filteredData.hourly_activity.forEach((h) => { if (h.hour >= 0 && h.hour < 24) values[h.hour] = h.events; });
    return {
      ...chartTheme,
      tooltip: { ...chartTheme.tooltip, trigger: "axis" },
      xAxis: { type: "category", data: Array.from({ length: 24 }, (_, i) => `${i}h`), axisLabel: { fontSize: 9, color: "#9ca3af", interval: 3 }, axisLine: { lineStyle: { color: "#f3f4f6" } } },
      yAxis: { type: "value", minInterval: 1, axisLabel: { fontSize: 10, color: "#9ca3af" }, splitLine: { lineStyle: { color: "#f9fafb", type: "dashed" } } },
      series: [{
        type: "bar",
        data: values.map((v, i) => ({ value: v, itemStyle: { color: i >= 6 && i < 18 ? "#3b82f6" : i >= 18 && i < 22 ? "#8b5cf6" : "#6b7280", borderRadius: [2, 2, 0, 0] } })),
        barWidth: "60%",
      }],
    };
  }, [data]);

  const eventDistOption = useMemo(() => {
    if (!filteredData?.event_distribution?.length) return null;
    const total = filteredData.event_distribution.reduce((s, d) => s + d.count, 0);
    return {
      tooltip: { trigger: "item", formatter: (p) => `${p.name}<br/>${p.value} events (${((p.value/total)*100).toFixed(1)}%)`, backgroundColor: "rgba(255,255,255,0.97)", borderColor: "#e5e7eb", borderWidth: 1, textStyle: { color: "#1f2937", fontSize: 12 } },
      series: [{
        type: "pie", radius: ["35%", "68%"], center: ["50%", "50%"], avoidLabelOverlap: true, padAngle: 2,
        itemStyle: { borderRadius: 6, borderColor: "#fff", borderWidth: 2 },
        label: { show: true, formatter: "{b}\n{d}%", fontSize: 10, color: "#374151" },
        emphasis: { label: { show: true, fontSize: 13, fontWeight: "bold" }, itemStyle: { shadowBlur: 10, shadowColor: "rgba(0,0,0,0.15)" } },
        data: filteredData.event_distribution.map((d) => ({ value: d.count, name: getEventMeta(d.event_type).label, itemStyle: { color: getEventMeta(d.event_type).color } })),
      }],
    };
  }, [data]);

  const browserOption = useMemo(() => {
    const bd = filteredData?.browser_distribution;
    if (!bd?.length) return null;
    const total = bd.reduce((s, d) => s + d.count, 0);
    return {
      tooltip: { trigger: "item", formatter: (p) => `${p.name}<br/>${p.value} (${((p.value/total)*100).toFixed(1)}%)`, backgroundColor: "rgba(255,255,255,0.97)", borderColor: "#e5e7eb", borderWidth: 1, textStyle: { color: "#1f2937", fontSize: 12 } },
      series: [{ type: "pie", radius: ["40%", "68%"], center: ["50%", "50%"], avoidLabelOverlap: true, padAngle: 1, itemStyle: { borderRadius: 4, borderColor: "#fff", borderWidth: 2 }, label: { show: true, formatter: "{b}: {c}", fontSize: 10, color: "#374151" }, data: bd.map((d, i) => ({ value: d.count, name: d.browser, itemStyle: { color: COLORS[i % COLORS.length] } })) }],
    };
  }, [filteredData]);

  const deviceOption = useMemo(() => {
    if (!filteredData?.device_distribution?.length) return null;
    return {
      tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)", backgroundColor: "rgba(255,255,255,0.97)", borderColor: "#e5e7eb", borderWidth: 1, textStyle: { color: "#1f2937", fontSize: 12 } },
      series: [{ type: "pie", radius: ["40%", "68%"], center: ["50%", "50%"], avoidLabelOverlap: true, padAngle: 1, itemStyle: { borderRadius: 4, borderColor: "#fff", borderWidth: 2 }, label: { show: true, formatter: "{b}: {c}", fontSize: 10, color: "#374151" }, data: filteredData.device_distribution.map((d, i) => ({ value: d.count, name: d.device, itemStyle: { color: COLORS[(i + 4) % COLORS.length] } })) }],
    };
  }, [filteredData]);

  const topPagesOption = useMemo(() => {
    if (!filteredData?.top_pages?.length) return null;
    const sorted = [...filteredData.top_pages].sort((a, b) => b.count - a.count).slice(0, 10);
    return {
      ...chartTheme,
      grid: { left: "3%", right: "10%", bottom: "3%", top: "3%", containLabel: true },
      xAxis: { type: "value", minInterval: 1, axisLabel: { fontSize: 10, color: "#9ca3af" }, splitLine: { lineStyle: { color: "#f9fafb", type: "dashed" } } },
      yAxis: { type: "category", data: sorted.map((p) => p.page.replace(/^https?:\/\/[^/]+/, "").length > 24 ? p.page.replace(/^https?:\/\/[^/]+/, "").substring(0, 24) + "..." : p.page.replace(/^https?:\/\/[^/]+/, "")).reverse(), axisLabel: { fontSize: 9, color: "#374151" }, axisLine: { lineStyle: { color: "#f3f4f6" } } },
      series: [{ type: "bar", data: sorted.map((p, i) => ({ value: p.count, itemStyle: { color: COLORS[i % COLORS.length], borderRadius: [0, 4, 4, 0] } })).reverse(), barWidth: "50%", label: { show: true, position: "right", fontSize: 10, fontWeight: "bold", color: "#374151", formatter: "{c}" } }],
    };
  }, [data]);

  const topProductsOption = useMemo(() => {
    if (!filteredData?.top_products?.length) return null;
    const sorted = [...filteredData.top_products].sort((a, b) => b.count - a.count).slice(0, 8);
    return {
      ...chartTheme,
      grid: { left: "3%", right: "10%", bottom: "3%", top: "3%", containLabel: true },
      xAxis: { type: "value", minInterval: 1, axisLabel: { fontSize: 10, color: "#9ca3af" }, splitLine: { lineStyle: { color: "#f9fafb", type: "dashed" } } },
      yAxis: { type: "category", data: sorted.map((p) => (p.product__equip_name || "Unknown").length > 26 ? (p.product__equip_name || "").substring(0, 26) + "..." : p.product__equip_name || "Unknown").reverse(), axisLabel: { fontSize: 9, color: "#374151" }, axisLine: { lineStyle: { color: "#f3f4f6" } } },
      series: [{ type: "bar", data: sorted.map((p, i) => ({ value: p.count, itemStyle: { color: COLORS[(i + 2) % COLORS.length], borderRadius: [0, 4, 4, 0] } })).reverse(), barWidth: "50%", label: { show: true, position: "right", fontSize: 10, fontWeight: "bold", color: "#374151", formatter: "{c}" } }],
    };
  }, [data]);

  const topButtonsOption = useMemo(() => {
    if (!filteredData?.top_buttons?.length) return null;
    return {
      ...chartTheme,
      grid: { left: "3%", right: "10%", bottom: "3%", top: "3%", containLabel: true },
      xAxis: { type: "value", minInterval: 1, axisLabel: { fontSize: 10, color: "#9ca3af" }, splitLine: { lineStyle: { color: "#f9fafb", type: "dashed" } } },
      yAxis: { type: "category", data: filteredData.top_buttons.map((b) => b.button_name.length > 26 ? b.button_name.substring(0, 26) + "..." : b.button_name).reverse(), axisLabel: { fontSize: 9, color: "#374151" }, axisLine: { lineStyle: { color: "#f3f4f6" } } },
      series: [{ type: "bar", data: filteredData.top_buttons.map((b, i) => ({ value: b.count, itemStyle: { color: COLORS[(i + 6) % COLORS.length], borderRadius: [0, 4, 4, 0] } })).reverse(), barWidth: "50%", label: { show: true, position: "right", fontSize: 10, fontWeight: "bold", color: "#374151", formatter: "{c}" } }],
    };
  }, [filteredData]);

  // ── World Map State ──
  const [worldMapLoaded, setWorldMapLoaded] = useState(false);
  const [worldMapError, setWorldMapError] = useState(false);

  // Fetch & register world map GeoJSON
  useEffect(() => {
    let mounted = true;
    const loadMap = async () => {
      // Check cache first
      const cached = sessionStorage.getItem("world_map_geojson");
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          echarts.registerMap("world", parsed);
          if (mounted) setWorldMapLoaded(true);
          return;
        } catch { /* fall through to fetch */ }
      }
      try {
        const res = await fetch("/assets/world.json");
        const geoJson = await res.json();
        echarts.registerMap("world", geoJson);
        sessionStorage.setItem("world_map_geojson", JSON.stringify(geoJson));
        if (mounted) setWorldMapLoaded(true);
      } catch (err) {
        console.error("Failed to load world map GeoJSON:", err);
        if (mounted) {
          setWorldMapError(true);
          setWorldMapLoaded(true); // prevent infinite loading
        }
      }
    };
    loadMap();
    return () => { mounted = false; };
  }, []);

  // ── Country name normalisation ──
  const COUNTRY_NAME_MAP = {
    USA: "United States",
    "U.S.A.": "United States",
    "U.S": "United States",
    UK: "United Kingdom",
    "U.K.": "United Kingdom",
    UAE: "United Arab Emirates",
    Russia: "Russian Federation",
    Iran: "Iran",
    Syria: "Syrian Arab Republic",
    Vietnam: "Viet Nam",
    "South Korea": "South Korea",
    "North Korea": "North Korea",
    "Czech Republic": "Czechia",
    Turkey: "Türkiye",
    "North Macedonia": "North Macedonia",
    Laos: "Lao PDR",
    Myanmar: "Myanmar",
    Tanzania: "Tanzania",
    Venezuela: "Venezuela",
    Bolivia: "Bolivia",
    "Ivory Coast": "Côte d'Ivoire",
    Moldova: "Moldova",
    "DR Congo": "Dem. Rep. Congo",
    "Congo": "Congo",
  };

  const normaliseCountry = (name) => COUNTRY_NAME_MAP[name] || name;

  // ── World Map choropleth option ──
  const countryMapOption = useMemo(() => {
    const cd = filteredData?.country_distribution;
    if (!worldMapLoaded || !cd?.length) return null;
    const countryData = cd.map(d => ({
      // eslint-disable-next-line no-undef
      name: normaliseCountry(d.country),
      value: d.count,
    }));
    const maxCount = Math.max(...countryData.map(d => d.value), 1);
    return {
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          if (!params.value) return `<strong>${params.name}</strong><br/>Visitors: <strong>0</strong>`;
          const total = countryData.reduce((s, d) => s + d.value, 0);
          return `<strong>${params.name}</strong><br/>Visitors: <strong>${params.value.toLocaleString()}</strong> (${((params.value / total) * 100).toFixed(1)}%)`;
        },
        backgroundColor: "rgba(255,255,255,0.97)",
        borderColor: "#e5e7eb",
        borderWidth: 1,
        textStyle: { color: "#1f2937", fontSize: 12 },
      },
      visualMap: {
        min: 0,
        max: maxCount,
        text: ["High", "Low"],
        realtime: false,
        calculable: true,
        inRange: {
          color: ["#e0f2f1", "#b2dfdb", "#80cbc4", "#4db6ac", "#26a69a", "#00897b", "#004d40"],
        },
        textStyle: { color: "#6b7280", fontSize: 10 },
        itemWidth: 12,
        itemHeight: 100,
        left: "left",
        bottom: 10,
      },
      series: [
        {
          type: "map",
          map: "world",
          roam: true,
          selectedMode: false,
          label: { show: false },
          emphasis: {
            label: {
              show: true,
              fontSize: 12,
              fontWeight: "bold",
              color: "#1f2937",
            },
            itemStyle: {
              areaColor: "#10b981",
              shadowBlur: 10,
              shadowColor: "rgba(16,185,129,0.3)",
            },
          },
          itemStyle: {
            borderColor: "#fff",
            borderWidth: 0.5,
            areaColor: "#f3f4f6",
          },
          data: countryData,
        },
      ],
    };
  }, [worldMapLoaded, filteredData]);

  const stateTreemapOption = useMemo(() => buildTreemap(filteredData?.state_distribution, "state", ["#8b5cf6","#6366f1","#3b82f6","#06b6d4","#14b8a6","#10b981","#84cc16","#f59e0b"]), [filteredData]);
  const cityTreemapOption = useMemo(() => buildTreemap(filteredData?.city_distribution, "city", ["#ec4899","#f97316","#f59e0b","#84cc16","#10b981","#06b6d4","#3b82f6","#6366f1"], 12), [filteredData]);

  // ── Browser pie subtitle indicator ──
  const browserSubtitle = browserFilter
    ? `Filtered: ${browserFilter}`
    : "Visitors by browser";

  // ── Loading (initial) ──
  if (loading && !data) return <Loader />;

  return (
    <section className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">

        {/* ═══ HEADER ═══ */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-gray-900 tracking-tight">
                Analytics Dashboard
              </h1>
              <p className="text-sm text-gray-400 mt-0.5">Real-time visitor and event metrics at a glance</p>
            </div>
            {/* Quick presets */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mr-1 hidden sm:inline">Quick:</span>
              {QUICK_PRESETS.map((p) => (
                <button key={p.days} onClick={() => handlePreset(p.days)}
                  className="px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800 transition-all active:scale-95">
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Filter Bar ── */}
          <div className="mt-3 sm:mt-4 bg-white rounded-xl sm:rounded-2xl border border-gray-100 shadow-sm p-3 sm:p-4">
            {/* Mobile: stacked, Tablet+: row */}
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-end gap-2 sm:gap-3">
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">From</label>
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                  className="h-9 rounded-lg border border-gray-200 bg-white px-2.5 text-sm text-gray-700 hover:border-gray-300 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 outline-none w-full transition-colors" />
              </div>
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">To</label>
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
                  className="h-9 rounded-lg border border-gray-200 bg-white px-2.5 text-sm text-gray-700 hover:border-gray-300 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 outline-none w-full transition-colors" />
              </div>
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Event</label>
                <select value={eventType} onChange={(e) => setEventType(e.target.value)}
                  className="h-9 rounded-lg border border-gray-200 bg-white px-2.5 text-sm text-gray-700 hover:border-gray-300 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 outline-none w-full appearance-none cursor-pointer transition-colors">
                  {EVENT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Country</label>
                <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}
                  className="h-9 rounded-lg border border-gray-200 bg-white px-2.5 text-sm text-gray-700 hover:border-gray-300 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 outline-none w-full appearance-none cursor-pointer transition-colors">
                  <option value="">All Countries</option>
                  {countryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Browser</label>
                <select value={browserFilter} onChange={(e) => setBrowserFilter(e.target.value)}
                  className="h-9 rounded-lg border border-gray-200 bg-white px-2.5 text-sm text-gray-700 hover:border-gray-300 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 outline-none w-full appearance-none cursor-pointer transition-colors">
                  <option value="">All Browsers</option>
                  {browserOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div className="flex gap-2 sm:pt-5">
                <button onClick={handleApply}
                  className="h-9 px-4 sm:px-5 rounded-lg bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-sm font-semibold hover:from-teal-600 hover:to-cyan-700 transition-all shadow-sm hover:shadow flex items-center gap-1.5 active:scale-95">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                  Apply
                </button>
                {filtersActive && (
                  <button onClick={handleReset}
                    className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-all active:scale-95">
                    Reset
                  </button>
                )}
              </div>
            </div>
            {/* Summary badge */}
            <div className="mt-2 sm:mt-3 text-xs text-gray-400">
              {data ? (
                <span className="inline-flex items-center gap-1.5 bg-gray-50 px-3 py-1 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  <span className="font-medium text-gray-500">{data.events_over_time?.length || 0} days</span>
                  <span className="text-gray-300">·</span>
                  <span className="font-medium text-gray-500">{summary.total_events || 0} events</span>
                  {filtersActive && <span className="text-gray-300">· filtered</span>}
                </span>
              ) : "No data"}
            </div>
          </div>
        </div>

        {/* ═══ ERROR ═══ */}
        {error && (
          <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-red-600 text-sm mb-6 flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            {error}
          </div>
        )}

        {/* ═══ EMPTY ═══ */}
        {!error && !hasData && !loading && (
          <div className="flex flex-col items-center justify-center py-16 sm:py-24 text-gray-400">
            <svg className="w-16 h-16 sm:w-20 sm:h-20 mb-4 text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-500 mb-1">No Analytics Data</h3>
            <p className="text-sm text-gray-400 text-center max-w-sm">No data for the selected filters. Try adjusting date range or clearing filters.</p>
          </div>
        )}

        {/* ═══ DASHBOARD ═══ */}
        {hasData && (
          <>
            {/* ── Stats Row ── */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 lg:gap-4 mb-4 sm:mb-6">
              <StatCard icon={<svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>} label="Events" value={summary.total_events?.toLocaleString() || 0} trend={summary.total_page_views > 0 ? `${summary.total_page_views} page views` : undefined} color="from-teal-500 to-cyan-600" />
              <StatCard icon={<svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>} label="Visitors" value={summary.total_visitors || 0} trend={`${summary.logged_in_users || 0} logged in`} color="from-blue-500 to-indigo-600" />
              <StatCard icon={<svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>} label="Logged In" value={summary.logged_in_users || 0} trend={summary.total_visitors > 0 ? `${((summary.logged_in_users/summary.total_visitors)*100).toFixed(0)}% of visitors` : ""} color="from-emerald-500 to-green-600" />
              <StatCard icon={<svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 14v6m-3-3h6M6 10h2a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2zm10 0h2a2 2 0 002-2V6a2 2 0 00-2-2h-2a2 2 0 00-2 2v2a2 2 0 002 2zM6 20h2a2 2 0 002-2v-2a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2z" /></svg>} label="Guests" value={summary.guest_users || 0} trend={summary.total_visitors > 0 ? `${((summary.guest_users/summary.total_visitors)*100).toFixed(0)}% of visitors` : ""} color="from-gray-500 to-gray-600" />
              <StatCard icon={<svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>} label="Products" value={summary.unique_products || 0} trend="Interacted with" color="from-purple-500 to-violet-600" />
              <StatCard icon={<svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>} label="Daily Avg" value={data.events_over_time?.length > 0 ? (summary.total_events / data.events_over_time.length).toFixed(1) : 0} trend={`Over ${data.events_over_time?.length || 0} days`} color="from-amber-500 to-orange-600" />
            </div>

            {/* ── Chart Row 1: Two line charts side-by-side ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6">
              <ChartCard title="Daily Events" subtitle="Event count per day with trend line" option={eventsOverTimeOption} loading={loading} height={280} />
              <ChartCard title="Cumulative Growth" subtitle="Running total of all events over time" option={cumulativeOption} loading={loading} height={280} />
            </div>

            {/* ── Chart Row 2: Hourly Activity + Event Distribution ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6">
              <ChartCard title="Hourly Activity" subtitle="Event distribution across 24 hours (day/evening/night)" option={hourlyActivityOption} loading={loading} height={280} />
              <ChartCard title="Event Distribution" subtitle="Breakdown by event type" option={eventDistOption} loading={loading} height={280} />
            </div>

            {/* ── Chart Row 3: Browser + Device + Top Buttons ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6">
              <ChartCard title="Browser" subtitle={browserSubtitle} option={browserOption} loading={loading} height={250} />
              <ChartCard title="Device" subtitle="Visitors by device type" option={deviceOption} loading={loading} height={250} />
              <ChartCard title="Top Buttons" subtitle="Most clicked buttons" option={topButtonsOption} loading={loading} height={250} />
            </div>

            {/* ── Chart Row 4: Top Products + Top Pages ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6">
              <ChartCard title="Top Products" subtitle="Most interacted-with products" option={topProductsOption} loading={loading} height={320} />
              <ChartCard title="Top Pages" subtitle="Most visited pages" option={topPagesOption} loading={loading} height={320} />
            </div>

            {/* ── Geo Map + Treemaps ── */}
            <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6">
              <ChartCard title="World Visitor Map" subtitle={countryFilter ? `Highlighting: ${countryFilter}` : "Visitor distribution by country (choropleth)"} refreshing={refreshing}>
                {worldMapLoaded && !worldMapError && countryMapOption ? (
                  <ReactECharts option={countryMapOption} opts={chartOpts} style={{ height: 420 }} notMerge />
                ) : worldMapError ? (
                  <div className="flex items-center justify-center" style={{ height: 420 }}>
                    <div className="text-center">
                      <svg className="w-10 h-10 mx-auto text-gray-200 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <p className="text-xs text-gray-300">Map data failed to load</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center" style={{ height: 420 }}>
                    <div className="text-center">
                      <svg className="w-10 h-10 mx-auto text-gray-200 mb-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <p className="text-xs text-gray-300">Loading map...</p>
                    </div>
                  </div>
                )}
              </ChartCard>
            </div>

            {/* ── State & City Treemaps ── */}
            {(stateTreemapOption || cityTreemapOption) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6">
                {stateTreemapOption && (
                  <ChartCard title="State Distribution" subtitle={countryFilter ? `States in ${countryFilter}` : "Visitor breakdown by state"} refreshing={refreshing}>
                    <ReactECharts option={stateTreemapOption} opts={chartOpts} style={{ height: 300 }} notMerge />
                  </ChartCard>
                )}
                {cityTreemapOption && (
                  <ChartCard title="City Distribution" subtitle={countryFilter ? `Cities in ${countryFilter}` : "Visitor breakdown by city"} refreshing={refreshing}>
                    <ReactECharts option={cityTreemapOption} opts={chartOpts} style={{ height: 300 }} notMerge />
                  </ChartCard>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default UserAnalytics;
